import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { useInView } from 'react-intersection-observer';

import { InnerBlocks } from '../assets/components';

async function loadPolyfills() {
  if (typeof window.IntersectionObserver === 'undefined') {
    await import('intersection-observer');
  }
}

loadPolyfills();

export { name } from './story.json';
export { Story as renderer };

const cardTypes = {
  advanced: 'advanced',
  secondary: 'secondary',
  basic: 'basic',
};

export function RichText({ className, tagName: Tag, value, ...rest }) {
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: value }} {...rest} />;
}

function ExpansionControl({ switchExpansion, ...rest }) {
  return <i onClick={switchExpansion} {...rest} />;
}

function RelatedSDGControl({ ...rest }) {
  return <i {...rest}></i>;
}

export function Story({ data, ...rest }) {
  const { attributes, innerBlocks } = data;
  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px 200px 0px',
    threshold: 0,
  });
  const innerBlocksRef = useRef(null);
  const [expansion, setExpansion] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);
  const [scrollAmount, setScrollAmount] = useState(0);

  const {
    className,
    blockClass,
    category,
    tagline,
    content,
    buttonClass,
    useMouseScroll,
    relatedTabText,
    enableRelatedButton,
    url,
    bottomButtonText,
    prefix,
    mergeCells,
    expansionOnHover,
    transparentOverlay,
    enableImage,
    cardType,
    standalone,
    partOf,
    anchor,
  } = attributes;

  // console.log(attributes);

  const BlockWrapper = attributes.tagName;

  const style = inView && url && enableImage ? { backgroundImage: `url(${url})` } : {};

  const switchExpansion = () => {
    setRelatedOpen(false);
    setExpansion(!expansion);
  };

  const handleScrollButtonClick = () => {
    const elementViewHeight = innerBlocksRef.current.offsetHeight;

    const elementScrollHeight = innerBlocksRef.current.scrollHeight;

    const nextScrollAmount = scrollAmount + elementViewHeight;

    const nextPressWillOverflow =
      Math.abs(nextScrollAmount - elementScrollHeight) < elementViewHeight;

    if (canScrollDown) {
      innerBlocksRef.current.scroll({ top: nextScrollAmount, behavior: 'smooth' });
      setScrollAmount(nextScrollAmount);

      setCanScrollDown(!nextPressWillOverflow);
    } else {
      innerBlocksRef.current.scroll({ top: 0, behavior: 'smooth' });
      setScrollAmount(0);
      setCanScrollDown(true);
    }
  };

  const blockClasses = classnames(blockClass, className, {
    'has-bg-expansion-on-hover': expansionOnHover,
    'has-transparent-overlay': transparentOverlay,
    'has-no-background-image': !enableImage,
  });

  const backgroundImageClasses = classnames(`${prefix}__background`, {});

  const innerWrapClasses = classnames(`${prefix}__wrap`, {});

  const firstLayoutClasses = classnames(`${prefix}__layout ${prefix}__first-layout`);

  const secondLayoutClasses = classnames(`${prefix}__layout ${prefix}__second-layout`);

  const relatedTabClasses = classnames(`${prefix}__related-tab`);

  const readMoreButtonClasses = classnames(`${prefix}__read-more-button`);

  const innerBlocksClasses = classnames(`${prefix}__inner-blocks`, {
    'has-button-scroll': !useMouseScroll,
  });

  const scrollButtonClasses = classnames(`${prefix}__scroll-button`, 'fal', {
    'fa-chevron-circle-down': canScrollDown,
    'fa-chevron-circle-up': !canScrollDown,
  });

  const categoryClasses = classnames(`${prefix}__category`, {});

  const taglineClasses = classnames(`${prefix}__tagline`, {});

  const contentClasses = classnames(`${prefix}__paragraph`, 'custom-scrollbar');

  const bottomButtonTextWrapperClasses = classnames(`${prefix}__bottomButtonText`, {});

  const expansionControlClasses = classnames(`${prefix}__expansion-control`, {
    [buttonClass]: true,
  });

  const relatedSDGButtonClasses = classnames(
    `${prefix}__related-sdgs-button`,
    'fal fa-info-circle',
    {
      // [relatedSDGControlClass]: true,
      'dock-opened': relatedOpen,
    },
  );

  return (
    <BlockWrapper
      id={anchor}
      ref={ref}
      className={blockClasses}
      card-type={`${cardType}`}
      merge-right={`${mergeCells}`}
      {...rest}
      standalone={`${standalone}`}
      part-of={`${partOf}`}
    >
      <div className={backgroundImageClasses} style={style} />
      <CSSTransition in={expansion} timeout={500} classNames="expansion" appear={true}>
        <CSSTransition in={relatedOpen} timeout={500} classNames="related" appear={true}>
          <div className={innerWrapClasses} onDoubleClick={switchExpansion}>
            <div class={firstLayoutClasses}>
              <RichText className={categoryClasses} tagName="h7" value={category} />
              <RichText
                className={taglineClasses}
                tagName="h3"
                value={tagline}
                onClick={switchExpansion}
              />
              {!(cardType === cardTypes.advanced && mergeCells) && (
                <>
                <div className={contentClasses}>
                  <RichText tagName="p" value={content} />
                  {(enableRelatedButton) && (
                    <div ref={innerBlocksRef} className={innerBlocksClasses}>
                      <InnerBlocks blocks={innerBlocks} nowrap />
                    </div>
                  )}
                  {(bottomButtonText) && (
                    <div className={bottomButtonTextWrapperClasses}>
                      <RichText tagName="p" value={bottomButtonText} />
                      <i class="fal fa-chevron-right"></i>
                    </div>
                  )}
                </div>
                </>
              )}
              <ExpansionControl
                className={expansionControlClasses}
                switchExpansion={switchExpansion}
              />
            </div>

            <div className={secondLayoutClasses}>
              {!useMouseScroll && (
                <i class={scrollButtonClasses} onClick={() => handleScrollButtonClick()} />
              )}
            </div>
          </div>
        </CSSTransition>
      </CSSTransition>
    </BlockWrapper>
  );
}
